import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Typography, Row, Col, Card, message, Spin } from 'antd';
import { getHomeBanners } from '../../services/blobService';
import ProductList from '../../components/ProductList';
import Banner from '../../components/Banner';

const { Title, Text } = Typography;

const ProductSection = styled.div`
  padding: 40px 0;
  background: #fff;

  @media (max-width: 576px) {
    padding: 24px 0;
  }
`;

const FeatureSection = styled.div`
  padding: 60px 0;
  background: #F5F5F5;
  margin-top: 40px;

  @media (max-width: 576px) {
    padding: 32px 0;
    margin-top: 24px;
  }
`;

const FeatureCard = styled(Card)`
  text-align: center;
  border: none;
  background: transparent;
  
  .ant-card-body {
    padding: 24px;
  }

  .feature-icon {
    font-size: 48px;
    margin-bottom: 16px;
    display: inline-block;
    transition: transform 0.3s ease;
  }

  &:hover .feature-icon {
    transform: scale(1.1);
  }

  .ant-typography {
    margin-bottom: 8px;
  }

  @media (max-width: 576px) {
    .ant-card-body {
      padding: 16px;
    }

    .feature-icon {
      font-size: 36px;
      margin-bottom: 12px;
    }

    .ant-typography {
      &.ant-typography-h3 {
        font-size: 18px;
      }
    }
  }
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 40px;
`;

const HomePage = () => {
  const { t } = useTranslation();
  const [bannerImages, setBannerImages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        setLoading(true);
        const banners = await getHomeBanners();
        if (banners.length > 0) {
          setBannerImages(banners);
        } else {
          message.warning(t('home.banners.empty'));
        }
      } catch (error) {
        console.error('Home - Error fetching banners:', error);
        message.error(t('home.banners.error'));
      } finally {
        setLoading(false);
      }
    };

    fetchBanners();
  }, [t]);

  const features = [
    { key: 'quality', icon: '🌟' },
    { key: 'service', icon: '💫' },
    { key: 'price', icon: '💎' }
  ];

  if (loading) {
    return (
      <LoadingContainer>
        <Spin size="large" />
      </LoadingContainer>
    );
  }

  return (
    <>
      <Banner images={bannerImages} />

      <ProductSection>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
          <Title level={2} style={{ textAlign: 'center', marginBottom: 48 }}>
            {t('home.trending.title')}
          </Title>
          <ProductList />
        </div>
      </ProductSection>

      <FeatureSection>
        <Row justify="center" gutter={[48, 48]} style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
          {features.map(({ key, icon }) => (
            <Col key={key} xs={24} sm={8}>
              <FeatureCard>
                <div className="feature-icon">{icon}</div>
                <Title level={3}>{t(`home.features.${key}.title`)}</Title>
                <Text>{t(`home.features.${key}.desc`)}</Text>
              </FeatureCard>
            </Col>
          ))}
        </Row>
      </FeatureSection>
    </>
  );
};

export default HomePage;  